import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { create } from "zustand";
import { askAssistant, askScopedAssistant, useCreateAction, useWhoAmI, type AssistantResponse, type ChatTurn, type ScopedAssistantResponse } from "../lib/api";
import { useAssistantScope } from "../lib/assistantContext";
import { isoDatePlusDays } from "../lib/format";
import { FeedbackControl } from "./FeedbackControl";
import { ProvenanceBadge } from "./ui";

/** A turn's answer: OneSphere's own, or a module's scoped envelope (OneFleet AD-04) which carries `evidence`. */
type Turn = ChatTurn & { meta?: AssistantResponse; scoped?: ScopedAssistantResponse; error?: boolean };

type AssistantState = {
  open: boolean;
  turns: Turn[];
  toggle: () => void;
  setOpen: (v: boolean) => void;
  push: (t: Turn) => void;
  clear: () => void;
};

/** Conversation kept across pages for the session (zustand store; nothing persisted to the browser). */
export const useAssistantStore = create<AssistantState>((set) => ({
  open: false,
  turns: [],
  toggle: () => set((s) => ({ open: !s.open })),
  setOpen: (open) => set({ open }),
  push: (t) => set((s) => ({ turns: [...s.turns, t] })),
  clear: () => set({ turns: [] }),
}));

const SUGGESTIONS = ["s1", "s2", "s3", "s4"] as const;

export function AssistantButton() {
  const { t } = useTranslation();
  const { open, toggle } = useAssistantStore();
  return (
    <button onClick={toggle} aria-pressed={open}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${open ? "bg-violet-600 text-white" : "border border-violet-300 text-violet-700 hover:bg-violet-50"}`}>
      <span aria-hidden="true">✦</span> {t("assistant.title")}
    </button>
  );
}

/** The extra blocks of a module-scoped answer (OneFleet envelope): tools, evidence deep links, tables, warnings. */
function ScopedAnswer({ res, linkBase }: { res: ScopedAssistantResponse; linkBase?: string }) {
  const { t } = useTranslation();
  const source = res.source === "rule" ? "rule" : "ai";
  return (
    <div className="mt-2 space-y-2 border-t border-brand-grey-light pt-2" data-assistant-scoped={res.interaction_id}>
      <div className="flex flex-wrap items-center gap-1 text-[10px] text-brand-grey">
        <ProvenanceBadge source={source} />
        {res.source === "ai+rule" && <span className="badge-rule">ai+rule</span>}
        {res.tools.length > 0 && <span>{t("assistant.toolsUsed")}:</span>}
        {res.tools.map((tool, j) => (
          <span key={j} className="rounded bg-white px-1.5 py-0.5 text-brand-charcoal">{tool.name ?? tool.tool}{tool.ms != null ? ` ${tool.ms} ms` : ""}</span>
        ))}
        <span className="ml-auto">{res.model?.replace(/^(ollama|onefleet-mock)\//, "")} · {(res.latency_ms / 1000).toFixed(1)} s</span>
      </div>
      {res.tables.map((tb, i) => (
        <div key={i} className="overflow-x-auto rounded border border-brand-grey-light bg-white">
          <table className="w-full text-[11px]">
            <thead className="text-left text-[10px] uppercase text-brand-grey"><tr>{tb.columns.map((c) => <th key={c} className="px-2 py-1">{c}</th>)}</tr></thead>
            <tbody>{tb.rows.slice(0, 8).map((r, k) => <tr key={k} className="border-t border-brand-grey-light">{r.map((v, m) => <td key={m} className="px-2 py-1">{v == null ? "—" : String(v)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ))}
      {res.evidence.length > 0 && (
        <ul className="flex flex-wrap gap-1" data-assistant-evidence>
          {res.evidence.map((e, i) => {
            const to = e.deep_link ? (linkBase && !e.deep_link.startsWith(linkBase) ? `${linkBase}${e.deep_link}` : e.deep_link) : null;
            const label = `${e.kind}: ${e.ref}`;
            return (
              <li key={i}>
                {to ? <Link to={to} className="rounded border border-brand-grey-light bg-white px-2 py-0.5 text-[11px] text-brand-primary hover:underline" title={e.snippet ?? undefined}>{label} →</Link>
                    : <span className="rounded border border-brand-grey-light bg-white px-2 py-0.5 text-[11px]" title={e.snippet ?? undefined}>{label}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {res.warnings.length > 0 && <div className="text-[10px] text-status-amber">{res.warnings.join(" · ")}</div>}
      <FeedbackControl interactionId={res.interaction_id} size="xxs" />
    </div>
  );
}

export function AssistantPanel() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { open, turns, push, clear, setOpen } = useAssistantStore();
  const scope = useAssistantScope();
  const whoami = useWhoAmI();
  const create = useCreateAction();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Record<string, string>>({});
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setInput("");
    const history: ChatTurn[] = [...turns.filter((x) => !x.error).map(({ role, content }) => ({ role, content })), { role: "user", content: q }];
    push({ role: "user", content: q });
    setBusy(true);
    try {
      if (scope) {
        const res = await askScopedAssistant(scope.endpoint, history, i18n.language, { page: location.pathname, ...(scope.context?.() ?? {}) }, scope.headers?.() ?? {});
        push({ role: "assistant", content: res.text, scoped: res });
      } else {
        const res = await askAssistant(history, i18n.language, { page: location.pathname });
        push({ role: "assistant", content: res.text, meta: res });
      }
    } catch (e) {
      push({ role: "assistant", content: t("assistant.unavailable"), error: true });
      console.warn(e);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void ask(input); };

  const confirmProposal = (key: string, p: AssistantResponse["proposals"][number]) => {
    create.mutate(
      { problem: p.problem, owner_user: whoami.data?.user ?? "", due_date: isoDatePlusDays(p.due_in_days), priority: (p.priority as "Low" | "Medium" | "High" | "Critical") ?? "Medium",
        source_doctype: p.source_doctype ?? undefined, source_name: p.source_name ?? undefined },
      { onSuccess: (r) => setCreated((c) => ({ ...c, [key]: r.name })) },
    );
  };

  if (!open) return null;
  return (
    <aside className="fixed bottom-0 right-0 top-16 z-30 flex w-full max-w-md flex-col border-l border-t border-brand-grey-light bg-white shadow-2xl" aria-label={t("assistant.title")}>
      <header className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
        <div>
          <div className="flex items-center gap-2 font-heading text-sm font-semibold"><span className="text-violet-600">✦</span> {scope?.title ?? t("assistant.title")} <ProvenanceBadge source="ai" /></div>
          <div className="text-[11px] text-brand-grey">{scope?.subtitle ?? t("assistant.subtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clear} className="text-[11px] text-brand-grey hover:text-brand-charcoal">{t("assistant.clear")}</button>
          <button onClick={() => setOpen(false)} aria-label="close" className="rounded px-2 py-1 text-brand-grey hover:bg-brand-grey-light">✕</button>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {turns.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-brand-grey">{t("assistant.intro")}</p>
            <div className="flex flex-wrap gap-2" data-assistant-prompts={scope ? "scoped" : "default"}>
              {(scope?.quickPrompts ?? SUGGESTIONS.map((k) => t(`assistant.suggestions.${k}`))).map((label) => (
                <button key={label} onClick={() => void ask(label)}
                  className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs text-violet-800 hover:bg-violet-100">{label}</button>
              ))}
            </div>
          </div>
        )}
        {turns.map((turn, i) => (
          <div key={i} className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm ${turn.role === "user" ? "bg-brand-primary text-white" : turn.error ? "border border-status-red/40 bg-red-50 text-brand-charcoal" : "bg-[#F6F8FA] text-brand-charcoal"}`}>
              <div className="whitespace-pre-wrap leading-relaxed">{turn.content}</div>
              {turn.scoped && <ScopedAnswer res={turn.scoped} linkBase={scope?.linkBase} />}
              {turn.meta && (
                <div className="mt-2 space-y-2 border-t border-brand-grey-light pt-2">
                  <div className="flex flex-wrap items-center gap-1 text-[10px] text-brand-grey">
                    <ProvenanceBadge source={turn.meta.source === "ai" ? "ai" : "rule"} />
                    {turn.meta.tools.length > 0 && <span>{t("assistant.toolsUsed")}:</span>}
                    {turn.meta.tools.map((tool, j) => (
                      <span key={j} className={`rounded px-1.5 py-0.5 ${tool.status === 200 ? "bg-white text-brand-charcoal" : "bg-red-50 text-status-red"}`} title={JSON.stringify(tool.params)}>{tool.tool}</span>
                    ))}
                    <span className="ml-auto">{turn.meta.model?.replace(/^ollama\//, "")} · {(turn.meta.latency_ms / 1000).toFixed(1)} s</span>
                  </div>
                  <FeedbackControl interactionId={turn.meta.interaction_id} size="xxs" />
                  {turn.meta.proposals.map((p, j) => {
                    const key = `${turn.meta!.interaction_id}-${j}`;
                    return (
                      <div key={key} className="rounded-lg border border-violet-200 bg-white p-2 text-xs">
                        <div className="font-semibold text-violet-800">{t("assistant.proposal")}</div>
                        <div className="mt-0.5">{p.problem}</div>
                        <div className="mt-0.5 text-brand-grey">{t("proj.priority")}: {t(`priority.${p.priority}`, p.priority)} · {t("assistant.dueIn", { n: p.due_in_days })}</div>
                        {created[key] ? (
                          <div className="mt-1 font-semibold text-status-green">{t("exec.actionCreated")} {created[key]}</div>
                        ) : (
                          <button onClick={() => confirmProposal(key, p)} disabled={create.isPending || !whoami.data}
                            className="mt-1 rounded bg-brand-primary px-2 py-1 font-semibold text-white hover:bg-brand-deep disabled:opacity-50">{t("assistant.confirmAction")}</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="rounded-2xl bg-[#F6F8FA] px-3 py-2 text-sm text-brand-grey"><span className="animate-pulse">{t("assistant.thinking")}</span></div></div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={onSubmit} className="border-t border-brand-grey-light p-3">
        <div className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("assistant.placeholder")} disabled={busy}
            className="flex-1 rounded-md border border-brand-grey-light px-3 py-2 text-sm focus:border-violet-500 focus:outline-none disabled:opacity-60" />
          <button type="submit" disabled={busy || !input.trim()} className="rounded-md bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">{t("assistant.send")}</button>
        </div>
        <p className="mt-2 text-[10px] leading-snug text-brand-grey">{t("assistant.disclaimer")}</p>
      </form>
    </aside>
  );
}
